import { useState, useCallback, useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import {
  fetchVersions,
  createVersion as apiCreateVersion,
  restoreVersion as apiRestoreVersion,
  deleteVersion as apiDeleteVersion,
  diffVersions as apiDiffVersions,
  type VersionEntry,
} from '../api/versions'
import { createNadDebug } from '../utils/debugLog'
import { useActiveCanvasId } from '../contexts/ActiveCanvasIdContext'

const versionDeleteLog = createNadDebug('dw:canvas-version-history:delete')

/**
 * Canvas version history for the ACTIVE canvas.
 *
 * The canvas is read from context rather than taken as an argument so no call
 * site can forget it. Before this, every request omitted the canvas entirely
 * and the backend fell back to `agent` — saving a version on a secondary canvas
 * snapshotted the wrong one, and restoring OVERWROTE the agent canvas.
 */
export function useCanvasVersions(projectId: string | undefined) {
  const canvasId = useActiveCanvasId()
  const { t } = useTranslation('canvas')
  const [versions, setVersions] = useState<VersionEntry[]>([])
  const [head, setHead] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [diffText, setDiffText] = useState<string | null>(null)
  const [diffVersionId, setDiffVersionId] = useState<string | null>(null)
  const [isDiffLoading, setIsDiffLoading] = useState(false)

  // Which canvas the newest in-flight list request belongs to. Switching tabs
  // starts a second request while the first is still open; without this guard a
  // slower response for the PREVIOUS canvas can land last and paint that
  // canvas's version tree (and head) under the new tab — the same wrong-canvas
  // class of bug this hook was fixed for.
  const latestListCanvasRef = useRef(canvasId)
  // Same guard for the diff pane: a diff is requested against "current", which
  // means the CURRENT canvas, so a delayed diff for the canvas the user just
  // left is not merely stale — it is a diff against the wrong document.
  const latestDiffCanvasRef = useRef(canvasId)

  const refresh = useCallback(async () => {
    if (!projectId) return
    latestListCanvasRef.current = canvasId
    setIsLoading(true)
    try {
      const data = await fetchVersions(projectId, canvasId)
      if (latestListCanvasRef.current !== canvasId) return
      setVersions(data.versions)
      setHead(data.head)
    } catch {
      if (latestListCanvasRef.current !== canvasId) return
      toast.error(t('canvasVersions.loadFailed'))
    } finally {
      if (latestListCanvasRef.current === canvasId) setIsLoading(false)
    }
  }, [projectId, canvasId, t])

  useEffect(() => {
    // A diff is relative to the previous canvas's "current"; switching canvases
    // makes it meaningless, so drop it rather than leave it on screen.
    latestDiffCanvasRef.current = canvasId
    setDiffText(null)
    setDiffVersionId(null)
  }, [canvasId])

  useEffect(() => {
    refresh()
  }, [refresh])

  const createVersion = useCallback(
    async (label: string) => {
      if (!projectId) return
      try {
        const entry = await apiCreateVersion(projectId, canvasId, label)
        toast.success(t('canvasVersions.saved', { label: entry.label }))
        await refresh()
      } catch (err) {
        toast.error(t('canvasVersions.saveFailed'))
        throw err
      }
    },
    [projectId, canvasId, refresh, t],
  )

  const restoreVersion = useCallback(
    async (versionId: string) => {
      if (!projectId) return
      try {
        await apiRestoreVersion(projectId, canvasId, versionId)
        toast.success(t('canvasVersions.restored'))
        await refresh()
      } catch (err) {
        toast.error(t('canvasVersions.restoreFailed'))
        throw err
      }
    },
    [projectId, canvasId, refresh, t],
  )

  const deleteVersion = useCallback(
    async (versionId: string) => {
      const isCurrent = versionId === head
      const versionCount = versions.length
      const trace_id = `canvas-version-delete:${versionId}`
      if (!projectId) {
        versionDeleteLog({
          event: 'delete-skipped',
          trace_id,
          versionId,
          isCurrent,
          versionCount,
          reason: 'missing-project-id',
        })
        return
      }
      versionDeleteLog({
        event: 'delete-request',
        trace_id,
        projectId,
        canvasId,
        versionId,
        isCurrent,
        versionCount,
      })
      try {
        const result = await apiDeleteVersion(projectId, canvasId, versionId)
        const count = result.deleted.length
        versionDeleteLog({
          event: 'delete-success',
          trace_id,
          projectId,
          canvasId,
          versionId,
          isCurrent,
          versionCount,
          deletedCount: count,
          deletedVersionIds: result.deleted,
        })
        toast.success(t('canvasVersions.deleted', { count }))
        await refresh()
      } catch (err) {
        versionDeleteLog({
          event: 'delete-failure',
          trace_id,
          projectId,
          canvasId,
          versionId,
          isCurrent,
          versionCount,
          errorMessage: err instanceof Error ? err.message : String(err),
        })
        toast.error(t('canvasVersions.deleteFailed'))
        throw err
      }
    },
    [projectId, canvasId, refresh, head, versions.length, t],
  )

  const diffVersion = useCallback(
    async (versionId: string) => {
      if (!projectId) return
      latestDiffCanvasRef.current = canvasId
      setIsDiffLoading(true)
      setDiffVersionId(versionId)
      try {
        const result = await apiDiffVersions(projectId, canvasId, versionId, 'current')
        if (latestDiffCanvasRef.current !== canvasId) return
        setDiffText(result.diff)
      } catch (err) {
        if (latestDiffCanvasRef.current !== canvasId) return
        toast.error(t('canvasVersions.diffFailed'))
        setDiffText(null)
        setDiffVersionId(null)
        throw err
      } finally {
        if (latestDiffCanvasRef.current === canvasId) setIsDiffLoading(false)
      }
    },
    [projectId, canvasId, t],
  )

  const closeDiff = useCallback(() => {
    setDiffText(null)
    setDiffVersionId(null)
  }, [])

  return { versions, head, isLoading, refresh, createVersion, restoreVersion, deleteVersion, diffVersion, closeDiff, diffText, diffVersionId, isDiffLoading }
}
